import {
  DB_PREF,
  COOKIES_SECURED,
  COOKIES_DOMAIN,
  COOKIES_TOKEN_NAME,
  COOKIES_USER_NAME,
  COOKIES_USR,
  COOKIES_ROLE,
  COOKIES_SERVICE,
  COOKIES_USER_INFO,
} from "./constants";
import cookie from "js-cookie";

const token = cookie.get("oya_token");

export const app = {
  version: require("../../package.json").version,
  dbpref: "oya_",
  cookieDomain: process.env.REACT_APP_COOKIES_DOMAIN || "localhost",
};

export const setCookie = (
  name: string,
  value: any,
  expirationDays: number = 7,
  domain?: string,
  isSecure: boolean = false,
): void => {
  if (typeof document === "undefined") {
    return;
  }

  const cookieDomain = getCookieDomain(domain || COOKIES_DOMAIN);
  const secure =
    isSecure ||
    parseBoolean(COOKIES_SECURED) ||
    window.location.protocol === "https:";

  cookie.set(`${DB_PREF || ""}${name}`, value, {
    expires: expirationDays,
    path: "/",
    sameSite: "strict",
    secure,
    ...(cookieDomain ? { domain: cookieDomain } : {}),
  });
};

export const setCookieJson = (
  cname: string,
  cvalue: any,
  exdays = 7,
  domain?: string,
) => {
  setCookie(cname, JSON.stringify(cvalue), exdays, domain);
};

export const getBase64 = (img: any, callback: (params: any) => void) => {
  const reader = new FileReader();
  reader.addEventListener("load", () => callback(reader.result));
  reader.readAsDataURL(img);
};

export const formatPhoneNumber = (phone: string) => {
  return phone?.replace(/\s+/g, "");
};

export const redirect = (to: any) => {
  window.location.assign(String(to));
};

export const getQueryValue = (
  search: string,
  key: string,
): string | undefined => {
  const value = new URLSearchParams(search).get(key);
  return value || undefined;
};

export const getRedirectUrlWithToken = (
  appref: string | undefined,
  token: string | undefined,
): string | undefined => {
  if (!appref || !token || typeof window === "undefined") {
    return undefined;
  }

  const normalizedUrl = getAbsoluteUrl(appref);
  if (!normalizedUrl) {
    return undefined;
  }

  const currentUrl = new URL(window.location.href);
  const targetUrl = new URL(normalizedUrl);
  const isCurrentPage =
    targetUrl.origin === currentUrl.origin &&
    targetUrl.pathname === currentUrl.pathname;

  if (
    isCurrentPage ||
    (targetUrl.origin === currentUrl.origin && targetUrl.pathname === "/login")
  ) {
    return undefined;
  }

  targetUrl.searchParams.set("token", token);
  return targetUrl.toString();
};

const parseBoolean = (value: unknown): boolean => {
  return value === true || value === "true" || value === "1";
};

const getCookieDomain = (domain?: string): string | undefined => {
  if (!domain || typeof window === "undefined") {
    return undefined;
  }

  const currentHost = window.location.hostname;
  let cookieDomain = domain.trim();

  if (!cookieDomain) {
    return undefined;
  }

  try {
    cookieDomain = new URL(cookieDomain).hostname;
  } catch {
    cookieDomain = cookieDomain.split(":")[0];
  }

  const normalizedDomain = cookieDomain.replace(/^\./, "");

  if (
    normalizedDomain === "localhost" ||
    normalizedDomain === "127.0.0.1" ||
    (normalizedDomain !== currentHost &&
      !currentHost.endsWith(`.${normalizedDomain}`))
  ) {
    return undefined;
  }

  return cookieDomain;
};

const getAbsoluteUrl = (url: string): string | undefined => {
  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return undefined;
  }

  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  if (trimmedUrl.startsWith("//")) {
    return `${window.location.protocol}${trimmedUrl}`;
  }

  if (trimmedUrl.startsWith("/")) {
    return new URL(trimmedUrl, window.location.origin).toString();
  }

  const protocol = window.location.protocol === "https:" ? "https" : "http";
  return `${protocol}://${trimmedUrl}`;
};

export const handleLogout = (origin?: string | any) => {
  [
    COOKIES_TOKEN_NAME,
    COOKIES_USER_NAME,
    COOKIES_USR,
    COOKIES_ROLE,
    COOKIES_SERVICE,
    COOKIES_USER_INFO,
  ].forEach(removeCookie);

  window.localStorage.clear();
  if (origin) {
    window.location.href = `/login?appref=${encodeURIComponent(origin)}`;
  } else {
    window.location.href = "/login";
  }
};

const removeCookie = (name: string) => {
  const cookieDomain = getCookieDomain(COOKIES_DOMAIN);

  cookie.remove(name, { path: "/" });

  if (cookieDomain) {
    cookie.remove(name, { path: "/", domain: cookieDomain });
    cookie.remove(name, {
      path: "/",
      domain: cookieDomain.replace(/^\./, ""),
    });
  }
};

export const cookieValue = (name: string): string | undefined => {
  const cookies = document.cookie.split("; ");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const [cookieName, cookieVal] = cookie.split("=");
    if (cookieName === name) {
      return cookieVal;
    }
  }
  return undefined;
};

export const setStorage = (key: string, value: string) => {
  if (key && value) {
    localStorage.setItem(DB_PREF + key, value);
  }
};
export const getStorage = (key: string) => {
  const value = localStorage.getItem(DB_PREF + key);
  return value || "";
};

export const config = {
  Authorization: `Bearer ${token}`,
};

export const validateEmail = (
  rule: any,
  value: string,
  callback: (message?: string) => void,
) => {
  // Regular expression for basic email validation
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  if (!value || emailRegex.test(value)) {
    callback();
  } else {
    callback("Please enter a valid email address");
  }
};

export const getDaysUntilJwtExpiration = (jwtToken: string) => {
  const tokenParts = jwtToken.split(".");
  if (tokenParts.length !== 3) {
    throw new Error("Invalid JWT format");
  }

  const payload = JSON.parse(atob(tokenParts[1]));

  if (!payload.exp) {
    throw new Error("JWT does not contain an expiration claim (exp)");
  }

  const expirationTimestamp = payload.exp;
  const currentTimestamp = Math.floor(Date.now() / 1000);

  const secondsUntilExpiration = expirationTimestamp - currentTimestamp;
  const daysUntilExpiration = Math.ceil(
    secondsUntilExpiration / (60 * 60 * 24),
  );

  return daysUntilExpiration;
};
export const clearCookie = (name: string, path: string, domain?: string) => {
  var dm = domain || document.domain;
  var pt = path || "/";
  document.cookie =
    name + "=; expires=" + +new Date() + "; domain=" + dm + "; path=" + pt;
};
